import type { PaymentMethod } from "@/interfaces/paymentMethod";
import { cardBrandLabel } from "@/utils/payment-helper";

/**
 * The saved-card chip shown once a payment method is on file. Mirrors the
 * prototype's `#checkoutPaymentSaved` (Campaign-Builder-Master-Prototype.html,
 * markup ~line 10634, CSS ~line 7574).
 *
 * Renders the group's saved card as the API returns it — every field is nullable
 * there, since Stripe may not have reported a brand or expiry yet when the
 * `setup_intent.succeeded` webhook writes the row.
 */
export interface SavedCardChipProps {
  card: PaymentMethod;
  /** Opens the card picker beneath the chip. Stripe has no "edit card" — changing means choosing
   *  another saved card, or adding one. */
  onChange: () => void;
  /** False when the caller lacks `buy CREDIT`, which the add-a-card endpoint requires. */
  canChange: boolean;
}

function formatExpiry(month: number | null, year: number | null): string | null {
  if (month === null || year === null) return null;
  return `${String(month).padStart(2, "0")} / ${String(year).slice(-2)}`;
}

export default function SavedCardChip({ card, onChange, canChange }: SavedCardChipProps) {
  const expiry = formatExpiry(card.expMonth, card.expYear);

  return (
    <div className="flex items-center gap-3 rounded-btn border border-smoke bg-clear-dust px-3.5 py-3">
      <div className="flex h-6 min-w-9 shrink-0 items-center justify-center rounded-sm border border-smoke bg-white px-1 font-lato text-[9px] font-extrabold whitespace-nowrap text-rodeo-black">
        {cardBrandLabel(card.brand)}
      </div>
      <div className="flex-1 font-lato text-[13px] text-rodeo-black">
        <span className="block">•••• {card.last4 ?? "••••"}</span>
        {expiry && <span className="mt-0.5 block text-[11px] text-graphite">Expires {expiry}</span>}
      </div>
      {canChange && (
        <button
          type="button"
          onClick={onChange}
          className="cursor-pointer rounded-sm px-2 py-1 font-lato text-[11px] font-bold uppercase tracking-wide text-rodeo-black hover:bg-white"
        >
          Change
        </button>
      )}
    </div>
  );
}
